import * as CoughSessionActions from './CoughSessionsListActions';
import { ReprocessCoughSessionModalActionTypes } from './Modals/ReprocessCoughSessionModal/ReprocessCoughSessionModalActions';
import { QueryOrderDirection, Query, CoughSession, CoughSessionStatus } from '../../misc/api/apiTypes';
import { getQuerySorting, getQueryFilters } from '../../misc/components/queryTable/ReducerUtilities';


export interface Variables {
    orderByMember: string;
    orderByDirection: QueryOrderDirection;
    current: number;
    size: number;
    serialNumber: Array<string>;
    recordingStartDateTime: Array<string>;
    status: Array<string>;
}

export enum SubmitState {
    Form = 0,
    AuditTrail = 1,
    Submitting = 2,
    Loading = 3
}

export enum ReprocessState {
    Confirmation = 0,
    AuditTrail = 1,
    Complete = 2
}

export interface RecordActionModalState {
    record: any;
    isVisible: boolean;
    error?: string;
    isSubmitting: SubmitState;
}

export interface ReprocessCoughSessionModalState {
    isVisible: boolean,
    reprocessState: ReprocessState,
    error?: string,
    isFetching: boolean,
    auditAnnotation: string,
    coughSession: CoughSession,
    analysisSetsWithEvents?: string[]
}

export interface CoughSessionsListState {
    reprocessCoughSessionModal: ReprocessCoughSessionModalState;
    unassignDeviceModal: RecordActionModalState;
    trackingNumberModal: RecordActionModalState;
    postalModal: RecordActionModalState;
    primaryAnalysisSetModal: RecordActionModalState & { selectedSets: Array<string> };
    coughSessionsQuery: Query<CoughSession>;
    statusesQuery: Query<CoughSessionStatus>;
    serialNumberQuery: Query<CoughSession>;
}

export const initialReprocessCoughSessionModalState: ReprocessCoughSessionModalState = {
    isVisible: false,
    reprocessState: ReprocessState.Confirmation,
    error: null,
    isFetching: false,
    auditAnnotation: null,
    coughSession: null,
    analysisSetsWithEvents: null
}

export const initialRecordActionModal: RecordActionModalState = {
    record: null,
    isVisible: false,
    isSubmitting: SubmitState.Form,
    error: null,
}

export const initialPrimaryAnalysisSetModal: RecordActionModalState & { selectedSets: Array<string> } = {
    record: null,
    isVisible: false,
    isSubmitting: SubmitState.Form,
    error: null,
    selectedSets: [],
}

export const initialCoughSessionsListState: CoughSessionsListState = {
    unassignDeviceModal: initialRecordActionModal,
    trackingNumberModal: initialRecordActionModal,
    postalModal: initialRecordActionModal,
    primaryAnalysisSetModal: initialPrimaryAnalysisSetModal,
    reprocessCoughSessionModal: initialReprocessCoughSessionModalState,
    coughSessionsQuery: {
        Order: {
            Member: "Id",
            Direction: QueryOrderDirection.Ascending,
        },
        Page: {
            Current: 1,
            Length: 10,
        },
        Filters: []
    },
    statusesQuery: {
        Order: {
            Member: 'Id',
            Direction: QueryOrderDirection.Ascending,
        },
        Filters: [],
    },
    serialNumberQuery: {
        Members: ['DeviceSerialNumber'],
        Distinct: true,
        Order: {
            Member: 'DeviceSerialNumber',
            Direction: QueryOrderDirection.Ascending,
        },
        Filters: [],
    }
};

export function coughSessionsListReducer(state: CoughSessionsListState = initialCoughSessionsListState, action: CoughSessionActions.CoughSessionsListAction): CoughSessionsListState {
    switch (action.type) {
        case CoughSessionActions.CoughSessionsListActionType.FilterChange:
            const filters = getQueryFilters(state.coughSessionsQuery, action.member, action.condition, action.value);

            return {
                ...state,
                coughSessionsQuery: {
                    ...state.coughSessionsQuery,
                    Filters: filters,
                    Page: {
                        ...state.coughSessionsQuery.Page,
                        Current: 1,
                    }
                },
            };
        case CoughSessionActions.CoughSessionsListActionType.ClearCoughSessions:
            return {
                ...state,
                coughSessionsQuery: {
                    ...state.coughSessionsQuery,
                    Data: null,
                }
            };
        case CoughSessionActions.CoughSessionsListActionType.PageChange:
            return {
                ...state,
                coughSessionsQuery: {
                    ...state.coughSessionsQuery,
                    Page: {
                        ...state.coughSessionsQuery.Page,
                        Current: action.page
                    },
                },
            };
        case CoughSessionActions.CoughSessionsListActionType.SortChange:
            const order = getQuerySorting(state.coughSessionsQuery, action.member);

            return {
                ...state,
                coughSessionsQuery: {
                    ...state.coughSessionsQuery,
                    Order: order,
                },
            };
        case CoughSessionActions.CoughSessionsListActionType.UnassignDeviceModalVisiblility:
            return {
                ...state,
                unassignDeviceModal: {
                    isVisible: action.show,
                    record: action.record,
                    isSubmitting: SubmitState.Form,
                },
            };
        case CoughSessionActions.CoughSessionsListActionType.PrimaryAnalysisSetVisibility:
            return {
                ...state,
                primaryAnalysisSetModal: {
                    selectedSets: action.record ? action.record.CoughAnalysisSets.filter(x => x.Primary).map(x => x.Id) : [],
                    isVisible: action.show,
                    record: action.record,
                    isSubmitting: SubmitState.Form,
                },
            };
        case CoughSessionActions.CoughSessionsListActionType.ClearAllFilters:
            return {
                ...state,
                coughSessionsQuery: {
                    ...state.coughSessionsQuery,
                    Filters: [],
                },
            };
        case CoughSessionActions.CoughSessionsListActionType.LoadCoughSessions:
            return {
                ...state,
                coughSessionsQuery: action.data,
            };
        case CoughSessionActions.CoughSessionsListActionType.LoadStatuses:
            return {
                ...state,
                statusesQuery: action.data,
            };
        case CoughSessionActions.CoughSessionsListActionType.LoadSerialNumbers:
            return {
                ...state,
                serialNumberQuery: action.data,
            };
        case CoughSessionActions.CoughSessionsListActionType.TrackingNumberSetFailed:
            return {
                ...state,
                trackingNumberModal: {
                    ...state.trackingNumberModal,
                    error: action.error,
                },
            };
        case ReprocessCoughSessionModalActionTypes.ShowModal:
            return {
                ...state,
                reprocessCoughSessionModal: {
                    ...initialReprocessCoughSessionModalState,
                    isVisible: true,
                    isFetching: true,
                    coughSession: action.coughSession
                }
            };
        case ReprocessCoughSessionModalActionTypes.CloseModal:
            return {
                ...state,
                reprocessCoughSessionModal: initialReprocessCoughSessionModalState
            };
        case ReprocessCoughSessionModalActionTypes.Complete:
            return {
                ...state,
                reprocessCoughSessionModal: {
                    ...state.reprocessCoughSessionModal,
                    reprocessState: ReprocessState.Complete,
                    error: null,
                    isFetching: false
                }
            };
        case ReprocessCoughSessionModalActionTypes.ApplyAuditAnnotation:
            return {
                ...state,
                reprocessCoughSessionModal: {
                    ...state.reprocessCoughSessionModal,
                    reprocessState: ReprocessState.AuditTrail,
                    auditAnnotation: action.auditAnnotation,
                    error: null,
                    isFetching: false
                }
            };
        case ReprocessCoughSessionModalActionTypes.CheckForEventsStart:
            return {
                ...state,
                reprocessCoughSessionModal: {
                    ...state.reprocessCoughSessionModal,
                    isFetching: true,
                    error: null
                }
            };
        case ReprocessCoughSessionModalActionTypes.CheckForEventsDone:
            return {
                ...state,
                reprocessCoughSessionModal: {
                    ...state.reprocessCoughSessionModal,
                    isFetching: false,
                    error: null,
                    analysisSetsWithEvents: action.analysisSetsWithEvents
                }
            };
        case ReprocessCoughSessionModalActionTypes.CheckForEventsError:
            return {
                ...state,
                reprocessCoughSessionModal: {
                    ...state.reprocessCoughSessionModal,
                    isFetching: false,
                    error: action.error,
                    analysisSetsWithEvents: null
                }
            };
        default:
            return state;
    }
}
